/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [sidebar-entry-date.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 13.02.20, 11:50
 */

import React, {Fragment, useRef} from 'react';
import ReactDOMServer from 'react-dom/server';
import {Link} from 'react-router-dom';

import {GENERIC_SPACE, RESTRICTED_ACCESS, SHOP_SPACE} from "../../actions/spaces";
import {getImageServerSmallUrl, getUserAvatar, getUserAvatarByName} from "../../actions/environment";
import {BoxedLink,} from "../../components/navigation/buttons/nav-buttons";
import {ImageBoxBig} from "../../components/boxes/image-box-big";
import {DateTimeBoxSimple} from "../../components/boxes/date-boxes/date-time-box-long";
import {
    isCalendarDateWithinInterval,
    isDateWithinInterval,
    isSameDayFns,
    useLanguage
} from "../../components/configuration/configuration";
import {NavigationGroup, NavigationRow} from "../../components/navigation/layout/nav-layout";
import {ExpandMore, Group, LocalAtm, NotInterested, Shop} from "@material-ui/icons";
import {HealthAndSafetyIcon, MaskSolidIcon} from "../../components/navigation/icons/nav-icons";
import CalendarView from "./calendar-view";
import {cn} from "../../components/util/text-utils";
import {MarkDownText} from "../../components/toggler/markdown-text-toggler";
import SpaceAccessIcon from "../../components/navigation/icons/space-access-icon";
import {MembershipIconShort} from "../../components/navigation/icons/membership-icon";

// space is of type EVENT
const CalendarViewEntryPeekEvent = ({peekspace, overlay = false, className}) => {
    const subMenuRef = useRef(null);
    const Lang = useLanguage();

    const renderTooltipHtml = (peekspace) => {
        const access = peekspace.access === RESTRICTED_ACCESS ? <MaskSolidIcon/> : '';
        const type = peekspace.type === GENERIC_SPACE ? <Group/>
            : peekspace === SHOP_SPACE ? <Shop/>
                : <CalendarView/>;

        return peekspace.cover === null ? renderAvatar(peekspace) :
            <div className="cover-tooltip">
                <img src={getImageServerSmallUrl(peekspace.cover)}/>
                <span>{peekspace.name} {type} {access}</span>

            </div>;
    }

    const renderAvatar = (peekspace) => {
        return <div className="avatar-tooltip">
            <span title={peekspace.name}>
                <img src={getUserAvatarByName(peekspace.avatar, peekspace.firstname, peekspace.lastname)}/>
            </span>
        </div>
    }

    const toggle = (event) => {
        event.preventDefault();
        subMenuRef.current && subMenuRef.current.classList.toggle('active-show');
        setTimeout(() => {
            if (document.activeElement !== document.body) document.activeElement.blur();
        }, 500);
    };

    const renderSubMenuChildren = (peekspace) => {
        const children = peekspace.children.map(child => {
            const target = `/${peekspace.username}/space/${child.id}`;
            return <li key={child.id} className="nav-item">
                <Link to={target} className="nav-link">{child.name}</Link>
            </li>
        });

        return <div className="sidebar-submenu">
            <ul className="nav flex-column">{children}</ul>
        </div>;
    }

    const calendarDate = peekspace.calendarDate;
    const calendarDateEnd = peekspace.calendarDateEnd;
    const activeSpace = `/${peekspace.username}/space/${peekspace.id}`;
    const html = ReactDOMServer.renderToStaticMarkup(renderTooltipHtml(peekspace));
    const isBlocked = peekspace.state === 'BLOCKED';
    const hasChildren = peekspace.children && peekspace.children.length > 0;
    const image = peekspace.url;
    const isBillable = peekspace.fees !== 'NONE';
    const isHygiene = peekspace.hygiene !== 'NONE';
    const isPrivate = peekspace.access === 'PRIVATE';
    const isCancelled = peekspace.state === 'CANCELLED';
    const isToday = isCalendarDateWithinInterval(new Date(),
        peekspace.calendarDate,
        peekspace.calendarDateEnd);

    return <div className={cn('sidebar-entry-event shadow mt-1 mb-2', [className])}>
        <NavigationRow className='sidebar-entry-date'>
            <NavigationGroup>
                <BoxedLink to={activeSpace}>
                    <ImageBoxBig blocked={isBlocked || isCancelled} image={image}
                                 overlay={overlay}
                                 isToday={isToday}
                                 category={peekspace.category}
                                 text={peekspace.name}/>
                </BoxedLink>

                {hasChildren && <div className="btn-children-toggle" onClick={toggle}>
                    <ExpandMore/>
                </div>}
                <div className="active-space-frame">
                    <div className="active-space-toggle" ref={subMenuRef}>
                        {hasChildren && renderSubMenuChildren(peekspace)}
                    </div>
                </div>

            </NavigationGroup>
        </NavigationRow>

        <div className='px-1 pb-1'>

            {isCancelled &&
                <div className='badge bg-danger rounded-pill small mt-1'>{Lang.info.status.cancelledShort}</div>}

            <DateTimeBoxSimple
                date={calendarDate.date}
                startTime={calendarDate.startTime}
                endTime={calendarDate.endTime}
                className={cn('mt-2', [!calendarDateEnd, 'mb-1'])}/>

            {calendarDateEnd && <DateTimeBoxSimple
                date={calendarDateEnd.date}
                startTime={calendarDateEnd.startTime}
                endTime={calendarDateEnd.endTime}
                isEndDate={true}
                className='mb-1'/>}

            <div className='sidebar-space-text lh-1'>{peekspace.name}
                <SpaceAccessIcon access={peekspace.access}/>
                <MembershipIconShort membership={peekspace.membership}/>
            </div>
            <div className='lh-125 mb-1'><MarkDownText text={peekspace.description}/></div>

            {!isCancelled && <div>
                <div className='box-aligned sidebar-space-title'>
                    <LocalAtm className="mr-1"/><span>{Lang.info.fees.text}</span>
                </div>
                <span className='text-smaller'>
                {isBillable ? <MarkDownText text={peekspace.feesDescription}/> : Lang.updateSpace.fees.free}
            </span>
                {isHygiene && <Fragment>
                    <div className='box-aligned sidebar-space-title'>
                        <HealthAndSafetyIcon className="mr-1"/><span>{Lang.info.hygiene.text}</span>
                    </div>

                    <span className='text-smaller'>
                    <MarkDownText text={peekspace.hygieneDescription}/>
                </span>
                </Fragment>}
                {isPrivate && <Fragment>
                    <MaskSolidIcon className="font-1x mr-1"/>
                    <span className='text-smaller'>
                    {Lang.info.access.privateShort}
                </span>
                </Fragment>}


            </div>}

            {isCancelled && <div>
                <span className='text-smaller text-bolder text-bg-yellow p-1'>
                     {Lang.info.status.cancelled}
                </span>
            </div>}

        </div>

    </div>
}

export default CalendarViewEntryPeekEvent;